/**
 * Return Order Header Component
 *
 * Displays the header of the return order modal: title and actions only.
 * Order info (ID, status, total, date) is shown once in ReturnOrderSummary to avoid duplication.
 */

import { Typography, IconButton, Tooltip, Skeleton, Paper, Divider } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import Stack from "src/shared/components/stack"
import { GetReturnOrderQuery } from "src/graphql/quark/quark.type"
import ReturnOrderReceipt from "src/modules/invoices/thermal/return-receipt"
import { useTypedSelector } from "src/shared/store/store"
import { useMemo, useRef } from "react"
import { mapReturnOrderQueryToReceiptData } from "src/modules/invoices/thermal/return-receipt/return-order-mapper"
import { useReactToPrint } from "react-to-print"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"

type ReturnOrderHeaderProps = {
  detail: GetReturnOrderQuery["getReturnOrder"]
  loading: boolean
  copied: boolean
  onCopyId: () => void
  onClose: () => void
}

const iconButtonSx = {
  transition: "all 0.2s ease",
  "&:hover": { transform: "scale(1.08)" }
}

export default function ReturnOrderHeader({ detail, loading, copied, onCopyId, onClose }: ReturnOrderHeaderProps) {
  const auth = useTypedSelector((state) => state.authentication)

  const printReceiptData = useMemo(() => mapReturnOrderQueryToReceiptData(detail), [detail])
  const componentRef = useRef<HTMLDivElement>(null)

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    pageStyle: `
      @page { size: 80mm auto; margin: 0mm; }
      @media print { body { margin: 0mm; } }
    `
  })

  return (
    <>
      <Stack variant="rowCenterSpaceBetween" sx={{ flexWrap: "wrap", gap: 1.5 }}>
        <Typography variant="h5" fontWeight={600} color="primary">
          Return Order Details
        </Typography>

        <Stack variant="rowCenter" gap={0.5} sx={{ flexWrap: "wrap" }}>
          {loading ? (
            <Stack variant="rowCenter" gap={0.5}>
              <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 1 }} />
            </Stack>
          ) : (
            <>
              {detail && (
                <>
                  <Tooltip title="Print receipt">
                    <IconButton size="small" onClick={() => handlePrint()} sx={iconButtonSx} aria-label="Print receipt">
                      <ReceiptLongIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={copied ? "Copied!" : "Copy return order ID"}>
                    <IconButton
                      size="small"
                      onClick={onCopyId}
                      color={copied ? "success" : "default"}
                      sx={iconButtonSx}
                      aria-label={copied ? "Copied" : "Copy ID"}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, alignSelf: "stretch" }} />
                </>
              )}
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={onClose}
                  sx={{
                    ...iconButtonSx,
                    "&:hover": { backgroundColor: "error.light", color: "white" }
                  }}
                  aria-label="Close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>

      <Paper elevation={0} sx={{ display: "none" }}>
        <ReturnOrderReceipt
          receiptData={{
            ...printReceiptData,
            logo: auth?.tenant?.invertedLogo as string,
            websiteUrl: auth?.tenant?.website ?? undefined,
            companyName: auth?.tenant?.name,
            locationName: auth?.currentWhLoc?.name,
            isDuplicateSlip: true
          }}
          receiptRef={componentRef}
        />
      </Paper>
    </>
  )
}
